import errorResolver from "./errorResolver";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import cloneDeep from "lodash.clonedeep";
import { CompactEvent, EventStatus, EventType, Visibility, FullEvent } from "../Common/types";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { RegistrationReason } from "../Common/registrationReasonConsts";
import { RegistrationChangesAfterDueDateStatus } from "./registrationStages";
import { BillingDetails } from "./federationsApi";
import HttpError from "./Utils/HttpError";

export type EventsForOrganizerToManage = {
    incomingEvents: CompactEvent[];
    pastEvents: CompactEvent[];
    draftEvents: CompactEvent[];
    hasAnyEvents: boolean;
    canCreateEvents: boolean;
};

export type DraftParameters = {
    timezone: string;

    federationId: string;
};

export type CreatedDraft = {
    eventId: number;
};

// Mirrors WizardRegistrationStageDto.php
export type WizardRegistrationStageDto = {
    durationDays: number;
    openDate?: string | null;
};

// Mirrors WizardCategoriesSetupMethod.php
export type WizardCategoriesSetupMethod = "template" | "copyFromPreviousEvent";

// Mirrors WizardCategoriesSetupRequest.php
export type WizardCategoriesSetupRequest = {
    method: WizardCategoriesSetupMethod;
    templateNames?: string[];
    categoryPaths?: string[];
    sourceEventId?: number;
};

// Mirrors WizardEventRequest.php
export type WizardEventRequest = {
    fullName: string;
    timezone: string;
    startDate: string | null;
    endDate: string | null;
    eventTypes: string[];
    country: string | null;
    place: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    registrationStages: WizardRegistrationStageDto[];
    categoriesSetup: WizardCategoriesSetupRequest | null;
};

export type ExtraContexts = {
    onlinePaymentsEnabled?: boolean;
    vouchersEnabled?: boolean;
    invitationForRegistration?: string;
    registrationChangesAfterDueDateStatus?: RegistrationChangesAfterDueDateStatus;
    // Mirrors CapEnforcementStatus.php via CapStatusExtraContext — absent whenever the cap doesn't apply.
    capStatus?: CapStatus;
};

export type EventSettings = {
    enablePaymentsAfterDueDate: boolean;
    enablePaymentConfirmationUploads: boolean;
    useBirthDateForCategoryMatching: boolean;
};

export type RegistrationStage = {
    id: number;
    startDate: string;
    endDate: string;
    totalDays: number;
};

export type CapStatus = {
    type: "warning" | "blocked";
    count: number;
    limit: number;
};

export type PublicEvent = {
    fullName: string;
    thumbnailImage: string | null;
    registrationStart: string;
    registrationEnd: string;
    registrationUpdatesArePossible: boolean;
    paymentsPossible: boolean;
    registrationOpen: boolean;
    registrationReason: RegistrationReason;
    eventSettings: Partial<EventSettings>;
    extraContexts: ExtraContexts;
    eventStartDate: string;
    eventEndDate: string | null;
    timezone: string;
    eventType: Array<EventType>;
    registrationStages: RegistrationStage[];
    place: string;
    internalInformation: string | null;
    country: string | null;
    isPrivate: boolean;
};

export type ContactInfo = {
    phone1: string | null;
    email1: string | null;
};

export type EventFile = {
    id: number;
    name: string;
    description: string | null;
    created: string;
};

export type ContactInfoUpdateRequest = {
    email1: string;
    phone1: string;
};

export type ChangeEventStatusRequest = {
    status?: EventStatus;
    visibility?: Visibility;
};

export type SubmitEventForReviewRequest = {
    billingDetails: BillingDetails;
    publishEventOnApproval: boolean;
};

export default {
    getIncomingEventsForUser(userId: number) {
        return promiseRequest({
            url: `/api/users/${userId}/events/incoming`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode !== 200) {
                throw Error("Could not fetch user's events.");
            }

            return res.body;
        });
    },
    getArchivedEventsForUser(userId: number) {
        return promiseRequest({
            url: `/api/users/${userId}/events/archive`,
            json: true,
            method: "get",
        }).then(res => {
            if (res.statusCode !== 200) {
                throw Error("Could not fetch user's archived events.");
            }

            return res.body;
        });
    },
    updateEventData(eventId: number, fullEvent: FullEvent): Promise<FullEvent> {
        const clonedFullEvent = cloneDeep(fullEvent);

        clonedFullEvent.event.eventDate.conversionSourceTimezone = clonedFullEvent.event.timezone;

        return promiseRequest<FullEvent>({
            url: `/api/admin/events/${eventId}`,
            method: "put",
            json: clonedFullEvent,
        }).then(res => {
            if (errorResolver.isSimpleErrorKey(res)) {
                throw Error(errorResolver.getSimpleErrorKey(res));
            }
            if (errorResolver.isValidationErrorResponse(res)) {
                const error = new HttpError("flash_messages.invalid_form", res.statusCode, res.body);
                error.validationErrors = errorResolver.getValidationErrorsFromResponse(res);
                throw error;
            }

            if (res.statusCode !== 200) {
                throw Error(`Could not update event ${eventId}.`);
            }

            return res.body;
        });
    },
    uploadImage(eventId: number, imageType: string, file: File) {
        const formData = new FormData();
        formData.append("imageType", imageType);
        formData.append("file", file, file.name);

        return promiseRequest({
            url: `/api/admin/events/${eventId}/images`,
            method: "POST",
            body: formData,
            headers: {
                Accept: "application/json",
            },
        }).then(res => {
            if (res.statusCode !== 204) {
                // @ts-expect-error Fix typing
                res.body = JSON.parse(res.body);
            }

            errorResolver.throwSimpleError(res);

            if (res.statusCode !== 204) {
                throw Error(`Could not upload image.`);
            }
        });
    },
    uploadEventFile(eventId: number, description: string, file: File) {
        const formData = new FormData();
        formData.append("description", description);
        formData.append("file", file, file.name);

        return promiseRequest({
            url: `/api/admin/events/${eventId}/files`,
            method: "POST",
            body: formData,
            headers: {
                Accept: "application/json",
            },
        }).then(resolveNoContent());
    },
    listEventFiles(eventId: number): Promise<EventFile[]> {
        return promiseRequest<EventFile[]>({
            url: `/api/admin/events/${eventId}/files`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    deleteEventFile(eventId: number, id: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/files/${id}`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    updateContactInfo(eventId: number, input: ContactInfoUpdateRequest) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/contact-info`,
            method: "put",
            json: input,
        }).then(resolveNoContent());
    },
    getAdminEvent(eventId: number): Promise<FullEvent> {
        return promiseRequest<FullEvent>({
            url: `/api/admin/events/${eventId}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getPublicEvent(eventId: number, requiredEventSettings: (keyof EventSettings)[] = []): Promise<PublicEvent> {
        const url =
            requiredEventSettings.length > 0
                ? `/api/events/${eventId}?requiredEventSettings=${requiredEventSettings.join(",")}`
                : `/api/events/${eventId}`;

        return promiseRequest<PublicEvent>({
            url: url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getIncomingEvents(): Promise<CompactEvent[]> {
        return promiseRequest<CompactEvent[]>({
            url: "/api/events",
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getIncomingEventsOfAnyVisibility(): Promise<CompactEvent[]> {
        return promiseRequest<CompactEvent[]>({
            url: "/api/events?include=upcoming-all",
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getAllEvents(): Promise<CompactEvent[]> {
        return promiseRequest<CompactEvent[]>({
            url: "/api/events?include=all",
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getEventsToManageForOrganizer(
        federationId?: string,
        fetchAllThatUserCanAccessAsFullOrganizer: boolean = false,
    ): Promise<EventsForOrganizerToManage> {
        const queryParams = new URLSearchParams();
        if (federationId) {
            queryParams.append("federationId", federationId);
        }
        if (fetchAllThatUserCanAccessAsFullOrganizer) {
            queryParams.append("fetchAll", "1");
        }

        return promiseRequest<EventsForOrganizerToManage>({
            url: `/api/admin/events?${queryParams.toString()}`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    createDraftEvent(draftParams: DraftParameters): Promise<CreatedDraft> {
        return promiseRequest<CreatedDraft>({
            url: `/api/admin/events`,
            json: draftParams,
            method: "post",
        }).then(resolveWithBody());
    },
    createWizardEvent(federationId: string, data: WizardEventRequest): Promise<CreatedDraft> {
        return promiseRequest<CreatedDraft>({
            url: `/api/admin/federations/${federationId}/wizard-events`,
            method: "POST",
            json: data,
        }).then(res => {
            if (errorResolver.isValidationErrorResponse(res)) {
                const error = new HttpError("flash_messages.invalid_form", res.statusCode, res.body);
                error.validationErrors = errorResolver.getValidationErrorsFromResponse(res);
                throw error;
            }
            if (res.statusCode !== 201) {
                throw new HttpError("Could not create event.", res.statusCode);
            }
            return res.body;
        });
    },
    // Captures wizard progress for the lead follow-up dashboard. Fire-and-forget: callers
    // must never let this disrupt the organizer flow (see PRD #894 §34.1).
    recordWizardProgress(federationId: string, furthestStep: number): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/wizard-progress`,
            method: "post",
            json: { federationId, furthestStep },
        }).then(resolveNoContent("Could not record wizard progress."));
    },
    getPublicEventContactInfo(eventId: number): Promise<ContactInfo> {
        return promiseRequest<ContactInfo>({
            url: `/api/events/${eventId}/public-contact-infos`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    revealEmail(eventId: number): Promise<{ email: string }> {
        return promiseRequest<{ email: string }>({
            url: `/api/events/${eventId}/reveal-email`,
            method: "post",
            json: true,
        }).then(resolveWithBody());
    },
    changeEventStatus(eventId: number, request: ChangeEventStatusRequest): Promise<FullEvent> {
        return promiseRequest<FullEvent>({
            url: `/api/admin/events/${eventId}/actions/change-status`,
            method: "put",
            json: request,
        }).then(resolveWithBody());
    },
    submitEventForReview(eventId: number, request: SubmitEventForReviewRequest): Promise<FullEvent> {
        return promiseRequest<FullEvent>({
            url: `/api/admin/events/${eventId}/actions/submit-for-review`,
            method: "put",
            json: request,
        }).then(resolveWithBody());
    },
};
